import { useState, useEffect, useCallback } from 'react';
import { Button, TextInput, Label } from 'flowbite-react';
import {
  Search,
  Mail,
  Phone,
  KeyRound,
  Eye,
  EyeOff,
  UserPlus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import { userApi } from '../../api/user';
import { useAuth } from '../../context/AuthContext';

const LIMIT = 10;

export default function FinanceOfficers() {
  const { tokens } = useAuth();
  const navigate = useNavigate();

  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Which officer's password form is open, plus its field state
  const [editingId, setEditingId] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchOfficers = useCallback(async () => {
    try {
      setLoading(true);
      const { status, data } = await userApi.getFinanceOfficers(
        tokens.access.token,
        page,
        LIMIT,
        debouncedSearch
      );

      if (status === 200) {
        setOfficers(data.rows || []);
        setTotalPages(Math.max(1, Math.ceil((data.count || 0) / LIMIT)));
      } else {
        toast.error(data?.message || 'Failed to load finance officers');
      }
    } catch (err) {
      console.error('Failed to load finance officers:', err);
      toast.error('Failed to load finance officers');
    } finally {
      setLoading(false);
    }
  }, [tokens.access.token, page, debouncedSearch]);

  useEffect(() => {
    fetchOfficers();
  }, [fetchOfficers]);

  // Debounce the search input
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  const openEditor = (officerId) => {
    setEditingId(officerId);
    setPassword('');
    setShowPassword(false);
  };

  const closeEditor = () => {
    setEditingId(null);
    setPassword('');
    setShowPassword(false);
  };

  const handleSavePassword = async (officer) => {
    if (!password) {
      toast.error('Please enter a new password');
      return;
    }
    if (password.length < 8 || !/\d/.test(password) || !/[a-zA-Z]/.test(password)) {
      toast.error('Password must be at least 8 characters and include a letter and a number');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Update password?',
      text: `Set a new password for ${officer.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0E64D1',
      confirmButtonText: 'Yes, update',
    });
    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);
      const response = await userApi.updatePassword(
        officer.id,
        tokens.access.token,
        password
      );

      if (response.status === 200) {
        Swal.fire({
          title: 'Success',
          text: 'Password updated successfully',
          icon: 'success',
        });
        closeEditor();
      } else {
        Swal.fire({
          title: 'Error',
          text: response.data?.message || 'Failed to update password',
          icon: 'error',
        });
      }
    } catch (err) {
      console.error('Failed to update password:', err);
      Swal.fire({
        title: 'Error',
        text: 'Something went wrong. Please try again.',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='bg-[#F8F9FB] min-h-screen pb-24'>
      <HeaderWithIcon title='Finance Officers' />

      <div className='px-4 py-3'>
        <div className='mb-3 flex items-center gap-2'>
          <TextInput
            className='flex-1'
            icon={Search}
            placeholder='Search by name or email...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button color='blue' onClick={() => navigate('/finance-officer/create')}>
            <UserPlus className='h-5 w-5' />
          </Button>
        </div>

        {loading ? (
          <div className='text-center py-10'>
            <div className='inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent'></div>
            <p className='mt-2 text-gray-600'>Loading finance officers...</p>
          </div>
        ) : officers.length === 0 ? (
          <div className='text-center py-10 text-gray-500'>
            No finance officers found.
          </div>
        ) : (
          <div className='space-y-3'>
            {officers.map((officer) => (
              <div
                key={officer.id}
                className='bg-white rounded-lg shadow-sm p-4'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='font-semibold text-gray-800 truncate'>
                      {officer.name}
                    </p>
                    <p className='mt-1 flex items-center gap-1 text-xs text-gray-500 truncate'>
                      <Mail className='h-3.5 w-3.5 shrink-0' />
                      {officer.email}
                    </p>
                    {officer.mobileNumber && (
                      <p className='mt-0.5 flex items-center gap-1 text-xs text-gray-500'>
                        <Phone className='h-3.5 w-3.5 shrink-0' />
                        {officer.mobileNumber}
                      </p>
                    )}
                  </div>
                  <Button
                    size='xs'
                    color={editingId === officer.id ? 'gray' : 'blue'}
                    onClick={() =>
                      editingId === officer.id
                        ? closeEditor()
                        : openEditor(officer.id)
                    }
                  >
                    <KeyRound className='mr-1 h-4 w-4' />
                    {editingId === officer.id ? 'Cancel' : 'Password'}
                  </Button>
                </div>

                {editingId === officer.id && (
                  <div className='mt-3 border-t pt-3'>
                    <Label
                      htmlFor={`password-${officer.id}`}
                      value='New Password'
                      className='mb-1 block'
                    />
                    <div className='relative'>
                      <TextInput
                        id={`password-${officer.id}`}
                        type={showPassword ? 'text' : 'password'}
                        icon={KeyRound}
                        placeholder='At least 8 characters'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type='button'
                        className='absolute inset-y-0 right-3 flex items-center text-gray-500'
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? (
                          <EyeOff className='h-5 w-5' />
                        ) : (
                          <Eye className='h-5 w-5' />
                        )}
                      </button>
                    </div>
                    <Button
                      color='blue'
                      className='mt-3 w-full'
                      onClick={() => handleSavePassword(officer)}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Update Password'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className='mt-4 flex items-center justify-center gap-4'>
            <Button
              size='sm'
              color='gray'
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className='h-4 w-4' />
            </Button>
            <span className='text-sm text-gray-600'>
              Page {page} of {totalPages}
            </span>
            <Button
              size='sm'
              color='gray'
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight className='h-4 w-4' />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
